import { useMemo, useState } from 'react';
import { DataTable } from '@customerio/pluma-components/react';
import { getUsageCount } from '../utils/usage';
import { hasDesignStudioUsage, isObjectOrRelationshipAttribute } from '../utils/item';
import type { DataIndexItemKind, DataIndexUsage, DataIndexUsageKey } from '../utils/types';
import { CampaignUsage, USAGE_ICONS, UsageEmptyState, usageColumnsFor } from './usage-columns';

/**
 * Where an item is used, one surface at a time.
 *
 * The saved views are the surfaces, each counted, so the size of the problem
 * is visible before anything is opened. The rows themselves are per-surface
 * and land with their own tables.
 */

/** What the workspace calls the surfaces that are named after a product noun. */
export type UsageViewTerms = {
  campaignsTitlePlural: string;
  newslettersTitlePlural: string;
};

export type UsageScope = {
  kind: DataIndexItemKind;
  /** Builder plans have no segments, campaigns or layouts. */
  isBuilder: boolean;
  /** Whether the workspace shows content usage for object and relationship attributes. */
  withTemplatesSnippetsLayouts: boolean;
};

export type UsageView = {
  id: DataIndexUsageKey;
  /** The surface's name on its own, for the caption and the empty state. */
  label: string;
  /** The name the view tab shows, which carries the count. */
  name: string;
};

/** Whether an item shows content usage at all. */
export function withContentUsage(scope: UsageScope): boolean {
  return isObjectOrRelationshipAttribute(scope.kind) ? scope.withTemplatesSnippetsLayouts : true;
}

function countedName(usage: DataIndexUsage, id: DataIndexUsageKey, label: string): string {
  return `${label} (${String(getUsageCount(usage, id))})`;
}

/**
 * The surface shown before anything is chosen. A Builder plan has no
 * campaigns, so its default is the next thing it does have.
 */
export function defaultUsageView(usage: DataIndexUsage, scope: UsageScope, terms: UsageViewTerms): UsageView {
  return scope.isBuilder
    ? {
        id: 'newsletters',
        label: terms.newslettersTitlePlural,
        name: countedName(usage, 'newsletters', terms.newslettersTitlePlural),
      }
    : {
        id: 'campaigns',
        label: terms.campaignsTitlePlural,
        name: countedName(usage, 'campaigns', terms.campaignsTitlePlural),
      };
}

/**
 * The surfaces offered beside the default one, counted, in the order they
 * are shown. The default is not among them: it is what the table falls back
 * to when no view is chosen.
 */
export function usageViews(usage: DataIndexUsage, scope: UsageScope, terms: UsageViewTerms): UsageView[] {
  const withContent = withContentUsage(scope);
  const views: UsageView[] = [];

  const named = (id: DataIndexUsageKey, label: string) => ({ id, label, name: countedName(usage, id, label) });

  if (!scope.isBuilder) {
    views.push(named('segments', 'Segments'));
    views.push(named('newsletters', terms.newslettersTitlePlural));
  }

  if (withContent) {
    views.push(named('templates', 'Templates'));
    if (hasDesignStudioUsage(scope.kind, withContent)) {
      views.push(named('design_studio', 'Design Studio'));
    }
    views.push(named('snippets', 'Snippets'));
    if (!scope.isBuilder) {
      views.push(named('layouts', 'Layouts'));
    }
  }

  return views;
}

type UsageTabProps = {
  workspaceId: number;
  /** The item's name, which the empty states say is not used anywhere. */
  itemName: string;
  usage: DataIndexUsage;
  scope: UsageScope;
  terms: UsageViewTerms;
};

export function UsageTab({ workspaceId, itemName, usage, scope, terms }: UsageTabProps) {
  // Which surface is being read is a glance, not a place: it is not worth a
  // URL, and Ember does not give it one either.
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const views = usageViews(usage, scope, terms);
  const defaultView = defaultUsageView(usage, scope, terms);
  const active = views.find((view) => view.id === activeViewId) ?? defaultView;
  const label = active.label;
  // Both rebuild the table, and the rows are the same rows until the view
  // changes.
  const rows = useMemo(() => [...(usage[active.id] ?? [])], [usage, active.id]);
  const columns = useMemo(() => usageColumnsFor(active.id, workspaceId), [active.id, workspaceId]);

  return (
    <DataTable
      caption={`${label} usage`}
      data={rows}
      columns={columns}
      // An attribute can be used by hundreds of campaigns. The page this
      // replaces holds them in a fixed-height scroll box and virtualizes;
      // paging is how the tables in this app answer the same problem.
      withPagination
      withSavedViews
      savedViews={{
        views,
        activeViewId,
        onActiveViewChange: setActiveViewId,
        defaultViewName: defaultView.name,
      }}
      // Only a campaign has more to say than its own row: which capacities
      // it uses the item in.
      withRowExpanding={active.id === 'campaigns'}
      getRowCanExpand={() => active.id === 'campaigns'}
      expandedRowComponent={CampaignUsage}
      emptyStateComponent={() => (
        <UsageEmptyState surface={active.id} label={label} itemName={itemName} icon={USAGE_ICONS[active.id]} />
      )}
    />
  );
}
